// M09 Bildschirm – Detail: Kopf (Name inline), Register Inhalt / Status / Ereignisse / Einstellungen, Entfernen.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { runCommand, type CommandType } from '../api/monitoring';
import { can, qk, useAssignments, useDevice, useGroups, useLocations, useMe, usePlaylists, useSchedules } from '../api/queries';
import type { DeviceDetail } from '../api/types';
import { Icon } from '../components/Icon';
import { DeviceMonitoringPanel } from '../components/Monitoring';
import { PlaylistThumb } from '../components/Preview';
import { useToast } from '../components/toast';
import { Confirm, Field, formatBytes, formatDateTime, PageHead, relativeTime, Spinner, StatusPill } from '../components/ui';
import { localeOf } from '../i18n';
import { deviceState, effectiveAssignment } from '../lib';
import { previewPath } from '../preview/open';
import { AssignDialog } from './AssignDialog';
import { ScreenPowerTab } from './Power';
import { hasFeature } from '../api/features';

type Tab = 'content' | 'status' | 'events' | 'settings' | 'monitoring' | 'power';

export function ScreenDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: d, isLoading, error } = useDevice(id);
  const { data: locations = [] } = useLocations();
  const { data: groups = [] } = useGroups();
  const { data: assignments = [] } = useAssignments();
  const { data: playlists = [] } = usePlaylists();
  const { data: schedules = [] } = useSchedules();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('content');
  const [name, setName] = useState('');
  const [assign, setAssign] = useState(false);
  const [remove, setRemove] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [cmdBusy, setCmdBusy] = useState<CommandType | null>(null);
  useEffect(() => setName(d?.name ?? ''), [d?.name]);

  if (isLoading) return <Spinner />;
  if (!d) return <div className="card muted">{error ? t('common.notFound') : ''}</div>;
  const manage = can(me, 'devices.manage') && d.status === 'active';

  const patch = async (body: Partial<Pick<DeviceDetail, 'name' | 'locationId' | 'orientation' | 'notes' | 'releaseChannel' | 'groupIds'>>) => {
    try {
      const upd = await api.patch<DeviceDetail>(`/devices/${d.id}`, body, { ifMatch: d.rowVersion });
      qc.setQueryData([...qk.device(d.id), me?.activeTenantId ?? 'none'], upd);
      await Promise.all([qc.invalidateQueries({ queryKey: qk.devices }), qc.invalidateQueries({ queryKey: qk.groups })]);
      toast.show(t('common.saved'));
    } catch (e) {
      toast.error(e);
      await qc.invalidateQueries({ queryKey: qk.device(d.id) });
    }
  };
  // Befehle an den Bildschirm (4.3): Ergebnis abwarten, sonst Hinweis „wird ausgeführt, sobald verbunden“
  const command = async (type: CommandType) => {
    setCmdBusy(type);
    try {
      const c = await runCommand(d.id, type, { timeoutMs: 20_000 });
      if (c.status === 'succeeded') toast.show(t(type === 'sync' ? 'monitoring.cmd.syncDone' : 'monitoring.cmd.reloadDone'));
      else if (c.status === 'failed' || c.status === 'expired') toast.error(new Error(t('monitoring.cmd.failed', { msg: c.message ?? c.status })));
      else toast.show(t('monitoring.cmd.pending'));
      await qc.invalidateQueries({ queryKey: qk.device(d.id) });
    } catch (e) {
      toast.error(e);
    } finally {
      setCmdBusy(null);
    }
  };
  const doRemove = async () => {
    try {
      const rev = await api.post<DeviceDetail>(`/devices/${d.id}/revoke`, { reason: 'Entfernt im Manager' });
      await api.del(`/devices/${d.id}`, { ifMatch: rev.rowVersion });
      await qc.invalidateQueries({ queryKey: qk.devices });
      toast.show(t('screen.removed'));
      navigate('/screens');
    } catch (e) {
      toast.error(e);
    }
  };

  const eff = effectiveAssignment(d, assignments, groups);
  const effName = eff ? (eff.assignment.playlistId ? playlists.find((p) => p.id === eff.assignment.playlistId)?.name : schedules.find((s) => s.id === eff.assignment.scheduleId)?.name) : null;
  const viaName = eff?.via === 'group' ? groups.find((g) => g.id === eff.viaId)?.name : eff?.via === 'location' ? locations.find((l) => l.id === eff.viaId)?.name : '';
  const platform = { android: 'Android', windows: 'Windows', linux: 'Linux', browser: 'Browser' }[d.platform];

  return (
    <>
      <PageHead
        title={manage ? (
          <input aria-label={t('common.name')} value={name} onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name !== d.name && void patch({ name: name.trim() })}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            style={{ fontSize: 22, fontWeight: 700, padding: '4px 8px', maxWidth: '100%' }} />
        ) : d.name}
        sub={<span className="row" style={{ gap: 8 }}><StatusPill state={deviceState(d)} />{platform} · {locations.find((l) => l.id === d.locationId)?.name ?? '–'}</span>}
        actions={<div className="row">
          <Link className="btn ghost" to="/screens"><Icon name="left" />{t('common.back')}</Link>
          {d.status === 'active' && <a className="btn" href={previewPath('screen', d.id)} target="_blank" rel="noopener" title={t('preview.openHint')}><Icon name="play" />{t('preview.open')}</a>}
          {can(me, 'devices.command') && d.status === 'active' && (
            <button className="btn" disabled={!!cmdBusy} onClick={() => void command('sync')}><Icon name="refresh" />{cmdBusy === 'sync' ? t('monitoring.cmd.running') : t('screen.sync')}</button>
          )}
          {can(me, 'devices.revoke') && <button className="btn danger" onClick={() => setRemove(true)}><Icon name="trash" />{t('screen.remove')}</button>}
        </div>}
      />
      {d.platform === 'browser' && <div className="hint warn" role="note" style={{ marginBottom: 12 }} data-testid="browser-limited"><b>{t('screen.browserLimited')}</b> {t('screen.browserLimitedText')}</div>}
      <div className="tabs" role="tablist">
        {(['content', 'status', 'monitoring', 'events', 'settings', 'power'] as Tab[]).filter((k) => (k !== 'monitoring' || (can(me, 'monitoring.read') && hasFeature(me, 'monitoring'))) && (k !== 'power' || hasFeature(me, 'power'))).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{k === 'monitoring' ? t('monitoring.screen.tab') : k === 'power' ? t('power.tab') : t(`screen.tab.${k}`)}</button>
        ))}
      </div>

      {tab === 'content' && (
        <div className="grid-2">
          <div className="card col">
            <h2 style={{ margin: 0 }}>{t('screen.effective')}</h2>
            {eff ? (
              <>
                <div><b>{effName ?? '–'}</b> {eff.assignment.scheduleId ? <span className="pill info plain">{t('schedules.one')}</span> : null}</div>
                <div className="muted small">{eff.via === 'device' ? t('screen.viaDevice') : eff.via === 'tenant' ? t('screen.viaTenant') : t('screen.via', { via: t(`assign.via.${eff.via}`), name: viaName ?? '' })}</div>
              </>
            ) : <div className="muted">{t('screen.noAssignment')}</div>}
            {can(me, 'assignments.manage') && <div><button className="btn primary" onClick={() => setAssign(true)}>{t('screen.ownAssignment')}</button></div>}
            {/* TODO M09: Zeitachse heute/7 Tage aus GET /devices/{id}/effective-content */}
          </div>
          <div className="card">
            <div className="tv">{eff?.assignment.playlistId ? <PlaylistThumb playlistId={eff.assignment.playlistId} /> : <span className="small">{t('screens.noContent')}</span>}</div>
          </div>
        </div>
      )}

      {tab === 'status' && (
        <div className="card">
          <dl className="props">
            <dt>{t('screen.connection')}</dt><dd><StatusPill state={deviceState(d)} /></dd>
            <dt>{t('screen.lastSeen')}</dt><dd>{d.lastSeenAt ? `${relativeTime(d.lastSeenAt, locale)} (${formatDateTime(d.lastSeenAt, locale)})` : '–'}</dd>
            <dt>{t('screen.playerVersion')}</dt><dd>{d.playerVersion ?? '–'}</dd>
            <dt>{t('screen.os')}</dt><dd>{d.osVersion ?? '–'}</dd>
            <dt>{t('screen.model')}</dt><dd>{d.hardwareModel ?? '–'}</dd>
            <dt>{t('screen.resolution')}</dt><dd>{d.resolution ? `${d.resolution.w} × ${d.resolution.h}` : '–'}</dd>
            <dt>{t('screen.storageFree')}</dt><dd>{d.state.storageFreeBytes !== null ? formatBytes(d.state.storageFreeBytes, locale) : '–'}</dd>
            <dt>{t('screen.cache')}</dt><dd>{d.state.cacheBytes !== null ? formatBytes(d.state.cacheBytes, locale) : '–'}</dd>
            <dt>{t('screen.clockSkew')}</dt><dd>{d.state.clockSkewMs !== null ? `${(d.state.clockSkewMs / 1000).toFixed(1)} s` : '–'}</dd>
            <dt>{t('screen.revision')}</dt><dd>{d.state.activeManifestRev ?? '–'}{d.state.pendingManifestRev && d.state.pendingManifestRev !== d.state.activeManifestRev ? ` → ${d.state.pendingManifestRev} (${d.state.syncProgress ?? 0} %)` : ''}</dd>
            <dt>{t('screen.playerState')}</dt><dd>{d.state.playerState ?? '–'}</dd>
            <dt>{t('screen.pairedAt')}</dt><dd>{formatDateTime(d.pairedAt, locale)}</dd>
            {d.revokedAt && <><dt>{t('status.revoked')}</dt><dd>{formatDateTime(d.revokedAt, locale)}</dd></>}
          </dl>
          {can(me, 'devices.command') && d.status === 'active' && (
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn sm" disabled={!!cmdBusy} onClick={() => void command('reloadEngine')}><Icon name="refresh" />{cmdBusy === 'reloadEngine' ? t('monitoring.cmd.running') : t('monitoring.cmd.reload')}</button>
            </div>
          )}
        </div>
      )}

      {tab === 'monitoring' && (
        <DeviceMonitoringPanel deviceId={d.id} online={d.connectivity === 'online'} canCommand={can(me, 'devices.command') && d.status === 'active'} canManage={can(me, 'devices.manage')} />
      )}

      {tab === 'events' && (
        <div className="card scroll">
          {d.events.length ? (
            <table className="list">
              <thead><tr><th>{t('screen.time')}</th><th>{t('screen.severity')}</th><th>{t('screen.code')}</th></tr></thead>
              <tbody>{d.events.map((e) => (
                <tr key={e.id}><td>{formatDateTime(e.occurredAt, locale)}</td>
                  <td><span className={`pill ${e.severity === 'error' || e.severity === 'critical' ? 'error' : e.severity === 'warning' ? 'warn' : 'neutral'}`}>{e.severity}</span></td>
                  <td className="mono small">{e.code}</td></tr>
              ))}</tbody>
            </table>
          ) : <div className="muted">{t('screen.noEvents')}</div>}
        </div>
      )}

      {tab === 'settings' && (
        <div className="card col" style={{ maxWidth: 640 }}>
          <Field label={t('screenAdd.location')}>
            <select disabled={!manage} value={d.locationId ?? ''} onChange={(e) => void patch({ locationId: e.target.value })}>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </Field>
          <Field label={t('screen.orientation')}>
            <select disabled={!manage} value={d.orientation} onChange={(e) => void patch({ orientation: e.target.value })}>
              {['landscape', 'portrait', 'landscape_flipped', 'portrait_flipped'].map((o) => <option key={o} value={o}>{t(`orientation.${o}`)}</option>)}
            </select>
          </Field>
          {groups.length > 0 && (
            <div className="field">{t('screenAdd.groups')}
              <div className="row">{groups.map((g) => (
                <label key={g.id} className="check"><input type="checkbox" disabled={!manage} checked={d.groupIds.includes(g.id)}
                  onChange={(e) => void patch({ groupIds: e.target.checked ? [...d.groupIds, g.id] : d.groupIds.filter((x) => x !== g.id) })} />{g.name}</label>
              ))}</div>
            </div>
          )}
          <Field label={t('screen.channel')}>
            <select disabled={!manage} value={d.releaseChannel} onChange={(e) => void patch({ releaseChannel: e.target.value as 'stable' | 'beta' })}>
              <option value="stable">Stable</option><option value="beta">Beta</option>
            </select>
          </Field>
          <Field label={t('screen.notes')}>
            <textarea disabled={!manage} defaultValue={d.notes ?? ''} onBlur={(e) => e.target.value !== (d.notes ?? '') && void patch({ notes: e.target.value || null })} />
          </Field>
        </div>
      )}

      {tab === 'power' && <ScreenPowerTab device={d} />}

      {assign && <AssignDialog targets={[{ targetType: 'device', targetId: d.id }]} title={t('screen.ownAssignment')} onClose={() => setAssign(false)} />}
      {remove && (
        <Confirm title={t('screen.removeTitle')} danger confirmLabel={t('screen.remove')} busy={confirmName !== d.name} onConfirm={doRemove}
          onClose={() => { setRemove(false); setConfirmName(''); }} text={t('screen.removeText')}>
          <Field label={t('screen.typeName', { name: d.name })}><input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} /></Field>
        </Confirm>
      )}
    </>
  );
}
